// The panels of one vendor: who they are (Company), how the platform works
// with them (Settings), and who may act for them (Members).
//
// They are the same panels wherever a vendor is looked at — the platform
// admin's vendors page and the vendor's own area on aldoo.com — so they live
// here rather than inside either of those pages.
import React, { useCallback, useEffect, useRef, useState } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Modal from "aldoo-ra/Modal"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import InviteUsersDialog from "aldoo-ra/Organizations/InviteUsersDialog"
import AddMediaDialog from "aldoo-ra/AddMediaDialog"
import { uploadOrgLogo, orgLogoUrl } from "aldoo-ra/Organizations/api"
import { SaveVendorProfile, DeleteOrg, RemoveMember, RemoveInvite, ResendInvite, BillingCodRestrict } from "../AdminShared/api"
import CountriesSelect from "../AdminShared/CountriesSelect"
import ShippingProvidersSection, { invalidProviderSetting } from "./ShippingProviders"
import ShopsSection from "./ShopsSection"
import ProductsVisibility from "./ProductsVisibility"
import PaymentLogos from "../Storefront/checkout/PaymentLogos"
import ReturnPolicyField, { returnPolicyErrors } from "./ReturnPolicyField"
import { returnPolicyModeOf, returnPolicyComplete, shipsWithProvider } from "../Vendor/EligibilityChecklist"

// a tab holding a missing detail: a red "!" on it, as the Company tabs have
const ALERT = { badge: "!", badgeClassName: "bg-admin_error text-white font-bold" }
const alertIf = (missing) => (missing ? ALERT : {})

import { Field, TextInput, TextArea, NumberInput, Select, Toggle, Section, Grid, Empty, Chip, Thumb, dateOnly, errorMessage } from "../AdminShared/ui"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { COMPANY } from "../Vendor/texts/bundles"
import Tabs from "aldoo-ra/Tabs"
import SectionPicker from "./SectionPicker"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"
import { useOrgRoles, RoleSelect, MemberAccessDialog, RolesSection, roleName } from "./MemberAccess"

import { useUrlParam } from "aldoo-ra/URLState"
import { usePayoutProfile, CompanyIdentity, CompanyAddress, EmailRole, PayoutEmailRole, BankAccount } from "../Payouts/CompanyParts"
import ContractTab, { useContract } from "../Payouts/ContractTab"
import { companyNameError, emailError, phoneError, ibanError } from "aldoo-ra/Store/contact-validation"
import { personNameError } from "aldoo-ra/Store/address-validation"
import { useStoreCurrency } from "aldoo-ra/Store/currency"
import useSiteLanguage from "../Storefront/hooks/useSiteLanguage"
import { currencyOptions } from "aldoo-ra/Store/currencies"
import { PaymentsSteps, useBilling } from "../Payouts/BillingCard"


// Who the vendor is, and how the platform works with them: two different
// questions, a tab each. `profile_save` takes whatever it is given, so each
// tab sends its own fields and leaves the other tab's alone.
// the order and stock notices' switch is on Contacts too (Order and stock notices), beside their address
const PROFILE_KEYS = ["name", "companyDisplayName", "contactName", "email", "salesEmail", "salesEmailMode", "publicEmail", "publicEmailMode", "phone", "iban", "notifyOnNewOrders", "notifyOnLowStock"]
const SETTINGS_KEYS = ["notifyOnNewOrders", "notifyOnLowStock", "lowStockThreshold", "orderLimitsEnabled", "maxQuantityPerOrder", "reportingTimeZone"]
// shipping: a tab of its own (ShippingEditor) — the couriers, where the
// company ships and its free-shipping offer — saved on its own
const SHIPPING_KEYS = ["shippingProviders", "shops", "shipsTo", "freeShippingThreshold", "minOrderAmount", "deliveryDays", "returnDays", "returnAddress", "returnPolicyMode", "returnPolicyUrl", "returnPolicyText", "autoCompleteDelivered", "autoCompleteDays"]
const SHIPS_TO_MODES = [
  { value: "worldwide", label: "Worldwide" },
  { value: "countries", label: "Selected countries only" },
]

/**
 * The zones a vendor keeps its books in. Whatever the browser offers, plus the
 * ones a shop here is likely to use — the figures are the account's, so they
 * must not follow whoever is reading them.
 */
const TIME_ZONES = (() => {
  const common = ["Europe/Sofia", "Europe/London", "Europe/Berlin", "Europe/Madrid", "America/New_York", "America/Los_Angeles", "UTC"]
  const all = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : common
  const here = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch {
      return null
    }
  })()
  const ordered = [...new Set([...(here ? [here] : []), ...common, ...all])]
  return ordered.map((zone) => ({ value: zone, label: zone.replace(/_/g, " ") }))
})()
const TOGGLES = new Set(["notifyOnNewOrders", "notifyOnLowStock"])

/**
 * The fields of one tab. There is no Save button: an edit is saved a moment
 * after it stops changing (a toggle at once, typing when it pauses), and the
 * answer — the organization as the server normalized it — is handed back, so
 * nothing is re-fetched and the panel is never torn down and rebuilt.
 */
const SAVE_DELAY = 700

/**
 * Which address a company's sales mail goes to: "contact" (its contact email)
 * or "own" (its salesEmail). Organizations from before the choice was saved
 * have none: one with a sales address chose its own, one without the contact.
 */
export const salesEmailModeOf = (org) => org?.salesEmailMode || (String(org?.salesEmail || "").trim() ? "own" : "contact")

/**
 * Which address customers see on the seller page: "contact", "sales" (the
 * sales address, when the company has one of its own) or "own" (publicEmail).
 * Unset: the sales address when there is one — what the page showed before
 * the choice — else the contact email (the API reads it the same way).
 */
export const publicEmailModeOf = (org) =>
  org?.publicEmailMode || (salesEmailModeOf(org) === "own" && String(org?.salesEmail || "").trim() ? "sales" : "contact")

/**
 * What a company still has to fill in for Aldoo to reach it, or null: the
 * contact email always, and the sales email when it chose an address of its
 * own. Without a contact email the sales mail has nowhere to go either.
 */
export const missingContactDetails = (org) => {
  if (!org) return null
  const contact = !!String(org.email || "").trim()
  const sales = salesEmailModeOf(org) === "contact" ? contact : !!String(org.salesEmail || "").trim()
  if (!contact && !sales) return "a contact email and a sales email"
  if (!contact) return "a contact email"
  if (!sales) return "a sales email"
  return null
}

// the form's value of a key: the saved one, or what an unsaved one means
const formValueOf = (org, key) => {
  if (key === "salesEmailMode") return salesEmailModeOf(org)
  if (key === "publicEmailMode") return publicEmailModeOf(org)
  // a company from before the choice with a link counts as having chosen the link
  if (key === "returnPolicyMode") return returnPolicyModeOf(org)
  if (key === "shipsTo") return { mode: org.shipsTo?.mode || "worldwide", countries: org.shipsTo?.countries || [] }
  if (key === "shippingProviders") return Array.isArray(org.shippingProviders) ? org.shippingProviders : []
  if (key === "shops") return Array.isArray(org.shops) ? org.shops : []
  // on, after 14 days, until the company says otherwise (the organization's defaults)
  if (key === "autoCompleteDelivered") return org.autoCompleteDelivered !== false
  if (key === "orderLimitsEnabled") return org.orderLimitsEnabled === true
  if (key === "autoCompleteDays") return org.autoCompleteDays || 14
  return org[key] ?? (TOGGLES.has(key) ? false : "")
}

/**
 * `validate(values)` is { key: message } for the fields that fail their rule
 * (aldoo-ra/Store/contact-validation, the rules profile_save applies): such a
 * field is shown with its message and left out of what is saved, so a wrong
 * phone number is never stored and never stops the name from saving. A field
 * the API refuses (`invalid_field`) is shown the same way.
 */
export function useVendorForm(org, keys, saved, onSaved, validate = null) {
  const t = usePortalTexts(COMPANY)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [errors, setErrorsState] = useState({})
  // the form as it is right now: `set` reads it to build what to send without
  // scheduling anything from inside a state updater (React may run one twice)
  const current = useRef({})
  const timer = useRef(null)
  const pending = useRef(null)
  const mounted = useRef(true)
  const failing = useRef({})
  const setErrors = (next) => {
    failing.current = next
    if (mounted.current) setErrorsState(next)
  }

  useEffect(() => {
    // an edit typed while the last one was being saved is newer than the
    // answer that just came back: it stays, and is saved in its turn
    if (pending.current) return
    // so does a field that was not saved because it is wrong: it stays as
    // typed, with its message, until it is put right
    const values = Object.fromEntries(keys.map((key) => [key, failing.current[key] ? current.current[key] : formValueOf(org, key)]))
    current.current = values
    setForm(values)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org])

  const send = async (values) => {
    pending.current = null
    const found = validate ? validate(values) : {}
    setErrors(found)
    const payload = Object.fromEntries(Object.entries(values).filter(([key]) => !found[key]))
    if (Object.keys(found).length) showNotification({ message: t`Not saved: fix the field marked in red`, className: "bg-admin_error text-white" })
    if (!Object.keys(payload).length) return
    if (mounted.current) setSaving(true)
    try {
      const res = await SaveVendorProfile(org._id, payload)
      if (res?.error === "invalid_field" && res.field) {
        // "shippingProviders.speedy.clientId" is the shipping providers' field
        const key = String(res.field).split(".")[0]
        const message = (validate && validate({ [key]: values[key] })[key]) || res.message || t`Request failed`
        setErrors({ ...found, [key]: message })
        showNotification({ message, className: "bg-admin_error text-white" })
        return
      }
      const error = errorMessage(res, t`Request failed`)
      if (!Object.keys(found).length) showNotification({ message: error || saved, className: error ? "bg-admin_error text-white" : "bg-admin_success text-white" })
      else if (error) showNotification({ message: error, className: "bg-admin_error text-white" })
      if (!error) onSaved(res?.org)
    } finally {
      if (mounted.current) setSaving(false)
    }
  }

  /** `now` skips the wait — a toggle has nothing more to type. */
  const set = (patch, { now = false } = {}) => {
    const next = { ...current.current, ...patch }
    current.current = next
    pending.current = next
    setForm(next)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => send(next), now ? 0 : SAVE_DELAY)
  }

  // leaving the field, the tab or the page must not lose what was just typed
  const flush = () => {
    if (!pending.current) return
    clearTimeout(timer.current)
    send(pending.current)
  }

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      flush()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { form, set, flush, saving, errors }
}

/**
 * The company profile's rules (aldoo-ra/Store/contact-validation, as
 * profile_save applies them), as { key: message } for the fields that fail.
 * A value the organization already has is not held against it — the API keeps
 * it as it is — so an old entry does not block the rest of the tab.
 */
const profileErrors = (org, values, t) => {
  const changed = (key) => values[key] !== undefined && values[key] !== (org?.[key] ?? "")
  const errors = {}
  if (changed("name") && companyNameError(values.name)) errors.name = t`Enter the company name: 2–120 characters, with letters, no < or >`
  // optional: empty clears it; given, a name like the company's, up to 80 characters
  if (changed("companyDisplayName") && String(values.companyDisplayName || "").trim() && companyNameError(String(values.companyDisplayName).slice(0, 80), { required: false })) errors.companyDisplayName = t`Enter a display name with letters, no < or >, up to 80 characters`
  if (changed("contactName") && personNameError(values.contactName)) errors.contactName = t`Enter the contact person's first and last name`
  if (changed("email") && emailError(values.email)) errors.email = t`Enter a valid email address, e.g. name@example.com`
  if (values.salesEmailMode === "own" && changed("salesEmail") && emailError(values.salesEmail, { required: false })) errors.salesEmail = t`Enter a valid email address, e.g. name@example.com`
  if (values.publicEmailMode === "own" && changed("publicEmail") && emailError(values.publicEmail, { required: false })) errors.publicEmail = t`Enter a valid email address, e.g. name@example.com`
  if (changed("phone") && phoneError(values.phone, { required: false })) errors.phone = t`Enter a valid phone number, e.g. +359 88 123 4567`
  if (changed("iban") && ibanError(values.iban)) errors.iban = t`This IBAN is not valid: check the country code, the length and the digits`
  return errors
}

export const SavingHint = ({ saving }) => {
  const t = usePortalTexts(COMPANY)
  return <span className="text-xs text-admin_text_secondary">{saving ? t`Saving…` : t`Saved automatically`}</span>
}

/**
 * The company: who the vendor is, and where their mail goes. `allowDelete`
 * is for the platform admin's page — a vendor working in their own area has
 * no business closing the organization from the same screen they edit their
 * phone number on.
 */
/**
 * The organization's logo: shown where the shop names the vendor, picked
 * through the media dialog (a file, a paste, a link, cropped square) and
 * stored by organizations {logo_upload}. Nothing until one is set.
 */
function LogoField({ org, canEdit, onSaved }) {
  const t = usePortalTexts(COMPANY)
  const [picking, setPicking] = useState(false)
  const [busy, setBusy] = useState(false)
  const upload = async (file, mimeType) => {
    setBusy(true)
    try {
      const res = await uploadOrgLogo(org._id, file, mimeType)
      const failed = errorMessage(res, t`Request failed`)
      if (failed) return { error: failed }
      return { ...res, url: orgLogoUrl(org._id, res?.logoUpdatedAt || Date.now()) }
    } finally {
      setBusy(false)
    }
  }
  const url = org?.logo ? orgLogoUrl(org._id, org.logoUpdatedAt) : null
  return (
    // not a <label> (Field): a label passes every click inside it to the button, the picture alone is the control
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-admin_text_secondary">{t`Logo`}</span>
      {/* the picture is the control: click it to pick a new one */}
      <button
        type="button"
        onClick={() => canEdit && !busy && setPicking(true)}
        disabled={!canEdit || busy}
        aria-label={url ? t`Change logo` : t`Add logo`}
        className="group relative self-start w-[84px] h-[84px] rounded-full overflow-hidden disabled:cursor-default"
        data-testid="org-logo-change"
      >
        <Thumb src={url} name={org?.name || "?"} className="w-full h-full rounded-full text-xl" />
        {canEdit && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/55 text-white text-[11px] font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
            {busy ? t`Uploading…` : url ? t`Change` : t`Add logo`}
          </span>
        )}
      </button>
      {picking && (
        <AddMediaDialog
          imagesOnly
          aspect={1}
          uploadFn={upload}
          onUse={(result) => {
            setPicking(false)
            if (result && !result.error) {
              showNotification({ message: t`Logo saved`, className: "bg-admin_success text-white" })
              onSaved?.({ logo: result.logo, logoUpdatedAt: result.logoUpdatedAt })
            }
          }}
          onCancel={() => setPicking(false)}
        />
      )}
      {canEdit && <span className="text-[11px] text-admin_text_secondary">{t`Click the picture to change it`}</span>}
    </div>
  )
}

/**
 * The Company section, in tabs:
 *   Details   the logo, the name (its company id under it), what kind of seller it is and its ids
 *   Address   the registered address
 *   Contacts  the contact person, the phone, the company email — and the company's other
 *             emails by their role (orders, customers, payouts), each the company email
 *             unless "use another email" is on
 *   Payments  the bank account payouts go to
 * The organization's fields save as they are edited (useVendorForm); so do the legal and
 * payout details (Payouts/CompanyParts), but the IBAN, saved on purpose. `payouts: false`:
 * a project without vendor payouts shows the organization's fields alone.
 */
export function ProfileEditor({ org, canEdit, productCount = 0, onSaved, onDeleted, allowDelete = true, payouts = true, admin = false }) {
  const t = usePortalTexts(COMPANY)
  // the company's fields saved: the payout profile is read again too — what it lacks follows them (its email is
  // the company's, so the Contacts tab's "!" goes once the company email is in)
  const payoutReload = useRef(null)
  const companySaved = useCallback((updated) => {
    onSaved?.(updated)
    payoutReload.current?.()
  }, [onSaved])
  const { form, set, flush, saving, errors } = useVendorForm(org, PROFILE_KEYS, t`Vendor profile saved`, companySaved, (values) => profileErrors(org, values, t))
  // a payout detail saved: the company's checklist and guide follow (onSaved without an org reloads the profile)
  const reloadProfile = useCallback(() => onSaved?.(), [onSaved])
  const payout = usePayoutProfile(org?._id, { enabled: payouts, onSaved: reloadProfile })
  payoutReload.current = payout.reload
  // the payout contract: its own tab here (Company → Contract), live
  const contractState = useContract(org?._id, { enabled: payouts })
  // the tab, in the address: a link (or a reload) lands on it
  const [tab, setTab] = useUrlParam("company", { default: "details" })

  // where the shop's own mail goes (a new order, a product running low), and what customers see
  const ownAddress = form.salesEmailMode === "own"
  const publicOwn = form.publicEmailMode === "own"
  const primary = String(form.email || "").trim()

  // what each tab still lacks (the payout profile's missing fields, the company's own form):
  // a tab holding one wears a red "!" — the contract's when it waits to be signed
  const missing = new Set(payout.profile?.missing || [])
  const lacking = {
    details: !String(form.name || "").trim() || ["entityType", "legalName", "companyId", "representative", "personalId", "dateOfBirth"].some((k) => missing.has(k)),
    address: ["country", "address"].some((k) => missing.has(k)),
    contacts: !primary || !String(form.contactName || "").trim() || !String(form.phone || "").trim() || (ownAddress && !String(form.salesEmail || "").trim()) || missing.has("email"),
    payments: !!payout.profile && !payout.profile.ibanLast4 && !payout.profile.stripeConnect?.accountId,
    contract: !!contractState.contract?.pending,
  }
  const alert = (key) => (lacking[key] ? { badge: "!", badgeClassName: "bg-admin_error text-white font-bold" } : {})
  const tabs = [
    { value: "details", label: t`Details`, ...alert("details") },
    ...(payouts ? [{ value: "address", label: t`Address`, ...alert("address") }] : []),
    { value: "contacts", label: t`Contacts`, ...alert("contacts") },
    ...(payouts ? [{ value: "payments", label: t`Payments`, ...alert("payments") }] : []),
    ...(payouts ? [{ value: "contract", label: t`Contract`, ...alert("contract") }] : []),
  ]
  const current = tabs.some((x) => x.value === tab) ? tab : "details"

  return (
    <>
    <Section title={<span className="inline-flex items-center gap-1.5">{t`Company`}<HelpHint helpKey="vendor.company" /></span>} actions={canEdit && <SavingHint saving={saving || payout.saving} />}>
      <SectionPicker tabs={tabs} value={current} onChange={setTab} title={t`Company`} testId="company-tabs-select" />
      <Tabs variant="admin" value={current} onChange={setTab} tabs={tabs} className="hidden lg:block" data-testid="company-tabs" />

      {current === "details" && (
        <div className="flex flex-col gap-4" data-testid="company-tab-details">
          <div className="flex items-start gap-1.5"><LogoField org={org} canEdit={canEdit} onSaved={onSaved} /><HelpHint helpKey="vendor.company#details" /></div>
          <Grid cols={2}>
            {/* the company id under the name: given once, when the organization is made — every
                product's pid carries it (product.<slug>.<cid>), so it stays what it is */}
            <Field label={t`Name`} error={errors.name} hint={org?.cid ? `${t`Company id (cid)`}: ${org.cid}` : undefined}>
              <TextInput value={form.name} onChange={(v) => set({ name: v })} onBlur={flush} maxLength={120} aria-invalid={errors.name ? true : undefined} disabled={!canEdit} data-testid="profile-name" />
            </Field>
            {/* what buyers see instead of the name: the store, the cart, their letters; links keep the company id */}
            <Field label={t`Marketplace display name`} error={errors.companyDisplayName} hint={t`Optional. Shown to buyers instead of the company name: on product pages, in the cart and in their order letters.`}>
              <TextInput value={form.companyDisplayName || ""} onChange={(v) => set({ companyDisplayName: v })} onBlur={flush} maxLength={80} placeholder={t`e.g. Robotev`} aria-invalid={errors.companyDisplayName ? true : undefined} disabled={!canEdit} data-testid="profile-display-name" />
            </Field>
          </Grid>
          {payouts && !payout.loading && <CompanyIdentity payout={payout} canEdit={canEdit} />}
          {canEdit && allowDelete && <DeleteOrgSection org={org} productCount={productCount} onDeleted={onDeleted} />}
        </div>
      )}

      {current === "address" && payouts && !payout.loading && <CompanyAddress payout={payout} canEdit={canEdit} />}

      {current === "contacts" && (
        <div className="flex flex-col gap-4" data-testid="company-tab-contacts">
          <Grid cols={2}>
            <Field label={<span className="inline-flex items-center gap-1.5">{t`Contact person *`}<HelpHint helpKey="vendor.company#contacts" /></span>} error={errors.contactName || (!String(form.contactName || "").trim() ? t`Required` : null)}><TextInput value={form.contactName} onChange={(v) => set({ contactName: v })} onBlur={flush} autoComplete="name" maxLength={100} aria-invalid={errors.contactName ? true : undefined} disabled={!canEdit} data-testid="profile-contact-name" /></Field>
            <Field label={t`Phone *`} error={errors.phone || (!String(form.phone || "").trim() ? t`Required` : null)}><TextInput value={form.phone} onChange={(v) => set({ phone: v })} onBlur={flush} type="tel" maxLength={20} placeholder="+359 88 123 4567" aria-invalid={errors.phone ? true : undefined} disabled={!canEdit} data-testid="profile-phone" /></Field>
            <Field label={t`Company email *`} error={errors.email} hint={t`The company's main email: the ones below use it unless you set another`}>
              <TextInput value={form.email} onChange={(v) => set({ email: v })} onBlur={flush} type="email" maxLength={254} aria-invalid={errors.email ? true : undefined} disabled={!canEdit} data-testid="contact-email" />
            </Field>
          </Grid>
          {/* the company's other emails, by what they are for */}
          <div className="flex flex-col gap-3">
            <EmailRole
              title={t`Email for customers`}
              hint={t`Shown on your seller page, for customers to reach you`}
              primary={primary}
              own={publicOwn}
              value={form.publicEmail}
              onChange={({ own, email }) => set(own ? { publicEmailMode: "own", ...(email !== undefined ? { publicEmail: email } : {}) } : { publicEmailMode: "contact", publicEmail: "" }, { now: true })}
              error={publicOwn ? errors.publicEmail : undefined}
              canEdit={canEdit}
              testId="public-email"
            />
            <EmailRole
              title={t`Order and stock notices`}
              hint={t`New orders and low stock notices go here`}
              primary={primary}
              own={ownAddress}
              value={form.salesEmail}
              onChange={({ own, email }) => set(own ? { salesEmailMode: "own", ...(email !== undefined ? { salesEmail: email } : {}) } : { salesEmailMode: "contact", salesEmail: "" }, { now: true })}
              error={ownAddress ? errors.salesEmail : undefined}
              canEdit={canEdit}
              testId="sales-email"
              // both notices at once: the new-order and the low-stock emails
              active={form.notifyOnNewOrders !== false || form.notifyOnLowStock !== false}
              onActiveChange={(on) => set({ notifyOnNewOrders: on, notifyOnLowStock: on }, { now: true })}
            />
            {payouts && !payout.loading && <PayoutEmailRole payout={payout} primary={primary} canEdit={canEdit} />}
          </div>
        </div>
      )}

      {/* (1) the bank account Aldoo pays into, (2) the card for the commission on cash on delivery sales —
          required to sell with it (Payouts/BillingCard) */}
      {current === "payments" && payouts && !payout.loading && (
        <PaymentsSteps bankAccount={<BankAccount payout={payout} canEdit={canEdit} orgId={org?._id} />} orgId={org?._id} canEdit={canEdit} codOn={org?.paymentMethods?.cashOnDelivery !== false} payoutIban={payout.profile?.ibanLast4} payoutSavedAt={payout.profile?.updatedAt} payoutMissing={!!payout.profile && !payout.profile.ibanLast4 && !payout.profile.stripeConnect?.accountId ? (payout.profile.stripeConnectAvailable ? "stripe" : true) : false} />
      )}
      {current === "contract" && payouts && <ContractTab orgId={org?._id} state={contractState} canSign={canEdit && !admin} admin={admin} />}
    </Section>
    </>
  )
}

/**
 * Closing an organization (Aldoo's, at the foot of Company → Details). It is a soft delete: the
 * organization leaves the listings while its members, products, orders and payouts stay as they
 * are — nothing about a sale that happened should disappear because the vendor closed. The dialog
 * says what stays, and asks for the organization's name, typed, so a slip does not delete one.
 */
export function DeleteOrgSection({ org, productCount, onDeleted }) {
  const t = usePortalTexts(COMPANY)
  const [asking, setAsking] = useState(false)
  return (
    <div className="mt-2 rounded-lg border border-admin_error/40 p-4 flex items-center justify-between gap-4" data-testid="org-delete-section">
      <div className="flex flex-col gap-1">
        <span className="font-semibold text-admin_error">{t`Delete organization`}</span>
        <Typography variant="admin_body2" className="my-0 text-admin_text_secondary">
          {t("Takes %s out of the vendors list. Its members, products, orders and payouts are kept.", org.name)}
        </Typography>
      </div>
      <Button variant="admin_secondary" onClick={() => setAsking(true)} className="border-admin_error text-admin_error hover:bg-admin_error hover:text-admin_text_inv shrink-0" data-testid="org-delete">
        {t`Delete`}
      </Button>
      {asking && <DeleteOrgDialog org={org} productCount={productCount} onClose={() => setAsking(false)} onDeleted={onDeleted} />}
    </div>
  )
}

function DeleteOrgDialog({ org, productCount, onClose, onDeleted }) {
  const t = usePortalTexts(COMPANY)
  const [typed, setTyped] = useState("")
  const [busy, setBusy] = useState(false)
  const name = String(org.name || "").trim()
  // the name as it is written (spaces around it aside): nothing else unlocks the delete
  const match = !!name && typed.trim() === name

  const productsKept = !productCount
    ? ""
    : ` ${t(
        productCount === 1
          ? "Its %s product stay in the store — unpublish them first if that is not what you want."
          : "Its %s products stay in the store — unpublish them first if that is not what you want.",
        productCount
      )}`

  const remove = async () => {
    if (!match || busy) return
    setBusy(true)
    const res = await DeleteOrg(org._id).catch((e) => ({ error: e.message }))
    setBusy(false)
    const error = errorMessage(res, t`Request failed`)
    showNotification({ message: error || t("%s deleted", org.name), className: error ? "bg-admin_error text-white" : "bg-admin_success text-white" })
    if (error) return
    onClose()
    onDeleted?.()
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      variant="admin"
      closeOnEscape
      title={t("Delete %s?", org.name)}
      styleOverrides={{ container: "w-[min(94vw,520px)] bg-admin_paper rounded-lg border border-admin_bordered shadow-lg" }}
      footerContent={
        <div className="flex gap-2 justify-end">
          <Button variant="admin_secondary" onClick={onClose} disabled={busy}>
            {t`Cancel`}
          </Button>
          <Button variant="admin_primary" onClick={remove} disabled={!match || busy} className="bg-admin_error border-admin_error hover:bg-admin_error/90" data-testid="org-delete-confirm">
            {busy ? t`Deleting…` : t`Delete`}
          </Button>
        </div>
      }
    >
      <div className="p-5 pt-1 flex flex-col gap-4" data-testid="org-delete-dialog">
        <Typography variant="admin_body2" className="my-0 text-admin_text_secondary">
          {`${t("%s leaves the vendors list and can no longer be edited or sold through.", org.name)}${productsKept} ${t`Its members, orders and payouts are kept, so this can be undone.`}`}
        </Typography>
        <Field label={t("Type %s to confirm", name)}>
          <TextInput value={typed} onChange={setTyped} onKeyDown={(e) => e.key === "Enter" && remove()} placeholder={name} autoFocus autoComplete="off" data-testid="org-delete-name" />
        </Field>
      </div>
    </Modal>
  )
}

/**
 * An amount of money, in minor units. It keeps what is being typed as text:
 * "12." is a halfway state on the way to "12.50", and a field that parses
 * every keystroke back into a number eats the decimal point.
 */
export function MoneyInput({ value, onChange, onBlur, disabled }) {
  const [draft, setDraft] = useState(null)
  const shown = draft ?? ((value || 0) / 100).toString()

  return (
    <TextInput
      inputMode="decimal"
      value={shown}
      disabled={disabled}
      onChange={(text) => {
        if (text !== "" && !/^\d*[.,]?\d{0,2}$/.test(text)) return
        setDraft(text)
        onChange(Math.max(0, Math.round((parseFloat(text.replace(",", ".")) || 0) * 100)))
      }}
      onBlur={() => {
        setDraft(null)
        onBlur?.()
      }}
      data-testid="free-shipping-amount"
    />
  )
}

/** Fulfilment and the mails that go with it. */
export function SettingsEditor({ org, canEdit, onSaved, defaultTimeZone, isPlatformAdmin = false, adminPage = false, physical = false }) {
  const t = usePortalTexts(COMPANY)
  const { form, set, flush, saving } = useVendorForm(org, SETTINGS_KEYS, t`Settings saved`, onSaved)
  // what is on the site, orders and stock, or reports — the tab in the address (settings=…)
  const [tab, setTab] = useUrlParam("settings", { default: "visibility" })
  const current = ["visibility", "orders", "reports"].includes(tab) ? tab : "visibility"
  const settingsTabs = [
    { value: "visibility", label: t`Visibility on the site` },
    // goods that travel need a low stock threshold (the company checklist)
    { value: "orders", label: t`Orders and stock`, ...alertIf(physical && !(Number(form.lowStockThreshold) > 0)) },
    { value: "reports", label: t`Reports` },
  ]

  return (
    <>
      <SectionPicker tabs={settingsTabs} value={current} onChange={setTab} title={t`Settings`} testId="settings-tabs-select" />
      <Tabs
        variant="admin"
        value={current}
        onChange={setTab}
        className="hidden lg:block"
        styleOverrides={{
          tabInactive: "text-admin_text_secondary hover:text-admin_text bg-admin_neutral/60 hover:bg-admin_paper/70 border-b-2 border-transparent",
        }}
        tabs={settingsTabs}
        data-testid="settings-tabs"
      />
      {/* the master switch: the whole catalog off the site, or Aldoo's hold on it; under it what is sold, by kind and type */}
      {current === "visibility" && <ProductsVisibility org={org} canEdit={canEdit} isPlatformAdmin={isPlatformAdmin} adminPage={adminPage} onSaved={onSaved} />}

      {current === "orders" && (
      <Section title={<span className="inline-flex items-center gap-1.5">{t`Orders and stock`}<HelpHint helpKey="vendor.settings#orders-and-stock" /></span>} actions={canEdit && <SavingHint saving={saving} />}>
        <Grid cols={2}>
          <Field label={<span className="inline-flex items-center gap-1.5">{t`Low stock threshold`}<HelpHint helpKey="vendor.settings#low-stock" /></span>} error={physical && !(Number(form.lowStockThreshold) > 0) ? t`Required` : null} hint={t`A product at or below this many left counts as low stock`}><NumberInput value={form.lowStockThreshold} onChange={(v) => set({ lowStockThreshold: v })} onBlur={flush} disabled={!canEdit} data-testid="low-stock-threshold" /></Field>
          {/* how many of one product a buyer may order at once — only once turned on; a product may set its own (Availability tab) */}
          <Field label={<span className="inline-flex items-center gap-1.5">{t`Limit quantities per order`}<HelpHint helpKey="vendor.settings#order-limits" /></span>} hint={t`Off: buyers order any quantity, whatever the products say. On: the limit below applies, and a product can set its own in its Availability tab.`}>
            <div className="flex items-center gap-3" data-testid="order-limits-toggle">
              <ToggleSwitch variant="admin" isOn={form.orderLimitsEnabled === true} onChange={(value) => canEdit && set({ orderLimitsEnabled: !!value }, { now: true })} />
              <span className="text-sm">{form.orderLimitsEnabled ? t`On` : t`Off`}</span>
            </div>
          </Field>
          {form.orderLimitsEnabled && (
            <Field label={t`Maximum items per product`} hint={t`Leave empty for no limit. A product can set its own limit in its Availability tab. Buyers who want more are shown your sales email.`}>
              <NumberInput value={form.maxQuantityPerOrder ?? ""} allowEmpty min={1} placeholder={t`No limit`} onChange={(v) => set({ maxQuantityPerOrder: v || null })} onBlur={flush} disabled={!canEdit} data-testid="max-quantity-per-order" />
            </Field>
          )}
        </Grid>
        {/* the order and low-stock emails are switched on and off by the orders email (Company → Contacts) */}
      </Section>
      )}

      {current === "reports" && (
      <Section title={<span className="inline-flex items-center gap-1.5">{t`Reports`}<HelpHint helpKey="vendor.settings#reports" /></span>}>
        <Grid cols={2}>
          <Field
            label={t`Reporting time zone`}
            hint={t`Sales are counted into days, weeks and months from midnight here — the same figures for everyone who reads them, wherever they are.`}
          >
            <Select
              value={form.reportingTimeZone || ""}
              options={TIME_ZONES}
              placeholder={t("Platform default (%s)", defaultTimeZone || "UTC")}
              onChange={(value) => set({ reportingTimeZone: value }, { now: true })}
              disabled={!canEdit}
              searchEnabled
              className="w-full"
              data-testid="reporting-time-zone"
            />
          </Field>
        </Grid>
      </Section>
      )}
    </>
  )
}

/** The couriers the company ships with, and the ways each delivers. */
/**
 * How buyers may pay for the company's products: online (card, PayPal, Apple Pay, Google Pay —
 * the store's payment form) and cash on delivery (наложен платеж), each on its own switch; at least
 * one stays on (vendor {profile_save: {paymentMethods}}).
 */
// `admin`: an Aldoo admin's view — may restrict the company's cash on delivery
export function PaymentMethodsEditor({ org, canEdit, admin = false, onSaved }) {
  const t = usePortalTexts(COMPANY)
  const { form, set, saving } = useVendorForm(org, ["paymentMethods"], t`Payment methods saved`, onSaved)
  // turning off the last one: a dialog says why it stays on
  const [lastOne, setLastOne] = useState(false)
  // both on unless turned off (a company that never chose has both)
  const methods = { online: form.paymentMethods?.online !== false, cashOnDelivery: form.paymentMethods?.cashOnDelivery !== false }
  // cash on delivery needs no card: the commission on it comes from the online payouts, the rest by card, debit
  // or by hand; an Aldoo admin may restrict it (its buyers then pay online only, and the vendor cannot switch it on)
  const { overview: billing, setOverview: setBilling } = useBilling(org?._id)
  const restricted = !!billing?.codRestricted
  const hasMethod = !!billing?.card?.valid || !!billing?.bank
  const restrict = async (on) => {
    const res = await BillingCodRestrict(org._id, on).catch((e) => ({ error: e.message }))
    if (res?.overview) setBilling(res.overview)
    if (!res?.result) return showNotification({ message: errorMessage(res) || t`Request failed`, className: "bg-admin_error text-white" })
    showNotification({ message: on ? t`Cash on delivery restricted` : t`Cash on delivery no longer restricted`, className: "bg-admin_success text-white" })
  }
  const paymentsLink = () => {
    const q = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "")
    q.set("section", "company")
    q.set("company", "payments")
    // in the admin the company's sections are its Profile tab's (the vendor portal's are its `section`)
    if (typeof window !== "undefined" && /^\/([a-z]{2}\/)?admin(\/|$)/.test(window.location.pathname)) q.set("tab", "profile")
    return `?${q.toString()}`
  }
  const turn = (key, on) => {
    if (key === "cashOnDelivery" && on && restricted) return
    const next = { ...methods, [key]: on }
    // the last one on stays on: a buyer must have a way to pay
    if (!next.online && !next.cashOnDelivery) return setLastOne(true)
    set({ paymentMethods: next }, { now: true })
  }
  const row = (key, title, hint, extra) => {
    // restricted by Aldoo: off for the buyers whatever the company chose
    const locked = key === "cashOnDelivery" && restricted
    const on = methods[key] && !locked
    return (
    <div className="flex items-start gap-4 rounded-md border border-admin_bordered p-4" data-testid={`payment-method-${key}`} data-on={on ? "on" : "off"}>
      <ToggleSwitch variant="admin" isOn={on} onChange={(next) => canEdit && !locked && turn(key, !!next)} />
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold">{title}</span>
          <span className={`text-xs ${on ? "text-admin_success font-semibold" : "text-admin_text_secondary"}`}>{on ? t`On` : t`Off`}</span>
        </div>
        <div className="text-xs text-admin_text_secondary">{hint}</div>
        {extra}
      </div>
    </div>
    )
  }
  return (
    <Section title={<span className="inline-flex items-center gap-1.5">{t`Payment methods`}<HelpHint helpKey="vendor.payment-methods" /></span>} subtitle={t`How buyers can pay for your products`} actions={canEdit && <SavingHint saving={saving} />}>
      <div className="flex flex-col gap-3" data-testid="payment-methods">
        {row("online", <span className="inline-flex items-center gap-1.5">{t`Online payments`}<HelpHint helpKey="vendor.payment-methods#online" /></span>, t`Card, PayPal, Apple Pay and Google Pay, paid at checkout and secured by Stripe`, <PaymentLogos className="mt-1" />)}
        {row(
          "cashOnDelivery",
          <span className="inline-flex items-center gap-1.5">{t`Cash on delivery`}<HelpHint helpKey="vendor.payment-methods#cash-on-delivery" /></span>,
          t`The buyer pays the courier when the parcel arrives (physical products only)`,
          <>
            {restricted ? (
              <div className="text-xs text-admin_error mt-1" data-testid="cod-restricted">{t`Restricted by Aldoo: your buyers pay online only. Contact Aldoo to have it switched on.`}</div>
            ) : billing?.hold && billing.hold.reason !== "card_expired" ? (
              <div className="text-xs text-admin_error mt-1" data-testid="cod-on-hold">
                {billing.hold.reason === "manual_overdue" ? t`On hold: a commission payment was not made in time. Pay it in Payouts → Commission.` : t`On hold: the commission could not be charged. Update the card in Company → Payments.`}
              </div>
            ) : (
              billing &&
              !hasMethod && (
                <div className="text-xs text-admin_text_secondary mt-1" data-testid="cod-manual">
                  {t`Aldoo's commission on these sales is taken from your online payouts; what they do not cover is paid by hand, from a proforma. A card in Company → Payments pays it automatically.`}{" "}
                  <a href={paymentsLink()} className="text-admin_primary underline">{t`Add a card`}</a>
                </div>
              )
            )}
            {/* Aldoo admins only: the company's cash on delivery restricted */}
            {admin && billing && (
              <div className="mt-2 pt-2 border-t border-admin_bordered/60" data-testid="cod-restrict-switch">
                <Toggle checked={restricted} onChange={(v) => restrict(!!v)} label={t`Restrict cash on delivery`} />
                <div className="text-[11px] text-admin_text_secondary mt-1">{t`Aldoo admins only. The company's buyers pay online only, and the company cannot switch cash on delivery on.`}</div>
              </div>
            )}
          </>
        )}
      </div>
      <Modal isOpen={lastOne} onClose={() => setLastOne(false)} title={t`Keep at least one payment method on`} variant="admin" closeOnEscape closeOnClickOutside className="max-w-[480px]">
        <div className="flex flex-col gap-4 p-1" data-testid="payment-methods-last">
          <p className="m-0 text-sm text-admin_text_secondary">{t`Buyers need a way to pay for your products, so one payment method always stays on. To use only the other one, turn it on first, then turn this one off.`}</p>
          <div className="flex justify-end">
            <Button variant="admin_primary" onClick={() => setLastOne(false)} data-testid="payment-methods-last-ok">
              {t`Got it`}
            </Button>
          </div>
        </div>
      </Modal>
    </Section>
  )
}

/** A company's name and address as lines for a parcel, or "" when the address is not there. */
function addressText(name, address, language = "bg") {
  if (!address?.line1 || !address?.city) return ""
  let country = address.country || ""
  try {
    if (country) country = new Intl.DisplayNames([language], { type: "region" }).of(country) || country
  } catch {}
  return [name, address.line1, address.line2, [address.postalCode, address.city].filter(Boolean).join(" "), country].filter((line) => String(line || "").trim()).join("\n")
}

export function ShippingEditor({ org, canEdit, onSaved, shippingCatalog, physical = false }) {
  const t = usePortalTexts(COMPANY)
  // a courier setting in the wrong format is marked on its field (ShippingProviders)
  // and the couriers are not saved until it is put right
  // so is a return policy without what its way needs (a link, a long enough text)
  const { form, set, flush, errors } = useVendorForm(org, SHIPPING_KEYS, t`Shipping saved`, onSaved, (values) => ({
    ...returnPolicyErrors(values, t),
    ...(values.shippingProviders !== undefined && invalidProviderSetting(values.shippingProviders, shippingCatalog) ? { shippingProviders: t`Not saved: fix the field marked in red` } : {}),
  }))
  // the company's address (Company → Address) as it goes on a parcel: the return address
  // in one tick; the same text is saved, so buyers and the checklist read it as any other
  const payoutProfile = usePayoutProfile(org?._id).profile
  const companyAddress = addressText(org?.name, payoutProfile?.address, useSiteLanguage())
  const [customReturn, setCustomReturn] = useState(false)
  const sameAsCompany = !!companyAddress && !customReturn && String(form.returnAddress ?? "").trim() === companyAddress

  // the threshold is kept in minor units, like every other price; the field
  // asks for it the way a price tag reads it
  // in the currency the store sells in unless the company picked another
  const { currency: storeCurrency, currencies } = useStoreCurrency()
  const threshold = form.freeShippingThreshold || {}
  const thresholdCurrency = threshold.currency || storeCurrency
  // the least an order from the company may come to (minor units, like the threshold): 0 is no minimum
  const minOrder = form.minOrderAmount || {}
  const minOrderCurrency = minOrder.currency || storeCurrency
  const setMinOrder = (patch, options) => set({ minOrderAmount: { amount: minOrder.amount || 0, currency: minOrderCurrency, ...patch } }, options)
  const setThreshold = (patch, options) => set({ freeShippingThreshold: { amount: threshold.amount || 0, currency: thresholdCurrency, ...patch } }, options)
  // where the company ships: everywhere, or the countries it names; a
  // product may say otherwise for itself (its Shipping tab)
  const shipsTo = form.shipsTo || { mode: "worldwide", countries: [] }
  const setShipsTo = (patch, options) => set({ shipsTo: { ...shipsTo, ...patch } }, options)
  // the delivery settings, or the couriers — the tab in the address (delivery=…)
  // the couriers first: they are set up before the rest (without one, physical goods are off the site)
  const [tab, setTab] = useUrlParam("delivery", { default: "providers" })
  const current = ["settings", "shops"].includes(tab) ? tab : "providers"
  // pickup from the shop on in the providers: the shops are where it goes (a red "!" while there is none open)
  const shopEntry = (form.shippingProviders || []).find((entry) => entry?.providerId === "shop")
  const shopOffered = !!shopEntry && shopEntry.enabled !== false && shopEntry.methods?.pickup?.enabled !== false
  const openShops = (form.shops || []).filter((shop) => shop.active !== false)

  return (
    <>
      <Tabs
        variant="admin"
        value={current}
        onChange={setTab}
        styleOverrides={{
          tabInactive: "text-admin_text_secondary hover:text-admin_text bg-admin_neutral/70 hover:bg-admin_paper/70 border-b-2 border-transparent",
        }}
        tabs={[
          // what the company checklist asks of delivery: a return address and policy (every company),
          // the return period and a courier (goods that travel)
          { value: "providers", label: t`Shipping providers`, ...alertIf(physical && !shipsWithProvider(form)) },
          { value: "shops", label: t`My shops`, ...alertIf(shopOffered && !openShops.length) },
          { value: "settings", label: t`Settings`, ...alertIf(!String(form.returnAddress || "").trim() || !returnPolicyComplete(form) || (physical && Number(form.returnDays) < 14)) },
        ]}
        data-testid="delivery-tabs"
      />
      {current === "providers" && (
      <ShippingProvidersSection
        orgId={org?._id}
        companyArea={{ shipsTo: form.shipsTo, deliveryDays: form.deliveryDays }}
        value={form.shippingProviders}
        catalog={shippingCatalog}
        canEdit={canEdit}
        onChange={(shippingProviders, options) => set({ shippingProviders }, options)}
        onFlush={flush}
        error={errors.shippingProviders}
        shopsCount={openShops.length}
        onOpenShops={() => setTab("shops")}
      />
      )}

      {current === "shops" && <ShopsSection value={form.shops} canEdit={canEdit} offered={shopOffered} onChange={(shops, options) => set({ shops }, options)} />}

      {current === "settings" && (
      <>
      {/* how long a parcel takes, and how long a buyer has to send it back */}
      {/* the return address, policy and period live here: marked while one is missing, folded or not */}
      <Section title={<span className="inline-flex items-center gap-1.5">{t`Delivery and returns`}<HelpHint helpKey="vendor.delivery#returns" /></span>} collapsible alert={!String(form.returnAddress || "").trim() || !returnPolicyComplete(form) || (physical && Number(form.returnDays) < 14)}>
        <Grid cols={2}>
          <Field label={t`Default delivery days (physical products)`}><NumberInput value={form.deliveryDays} onChange={(v) => set({ deliveryDays: v })} onBlur={flush} disabled={!canEdit} /></Field>
          {/* the law gives buyers 14 days from delivery to send goods back; a seller may give more, never less */}
          {/* not set yet: empty, 14 as the hint — a 14 shown but never saved was counted missing by the checklist */}
          <Field label={t`Return period (days from delivery)`} error={physical && !(Number(form.returnDays) >= 14) ? t`Required` : null} hint={t`At least 14 days, by law. Offer more if you like.`}>
            <NumberInput value={form.returnDays ?? ""} allowEmpty placeholder="14" onChange={(v) => set({ returnDays: v })} onBlur={flush} disabled={!canEdit} min={14} data-testid="return-days" />
          </Field>
          {/* where buyers send goods back: told to them when a return request is accepted */}
          <Field label={t`Physical items return address *`} error={!String(form.returnAddress ?? "").trim() ? t`Required` : null} hint={t`Where buyers send items back. Written as it goes on the parcel; the buyer gets it when their return request is accepted.`}>
            {/* the company's own address in one tick (Company → Address), or one written here */}
            {companyAddress && (
              <Toggle
                checked={sameAsCompany}
                onChange={(on) => (on ? (setCustomReturn(false), set({ returnAddress: companyAddress }, { now: true })) : setCustomReturn(true))}
                disabled={!canEdit}
                label={t`Same as the company address`}
                data-testid="return-address-same"
              />
            )}
            {sameAsCompany ? (
              <div className="whitespace-pre-line rounded-md border border-admin_bordered bg-admin_neutral_1/40 px-3 py-2 text-sm" data-testid="return-address">
                {companyAddress}
              </div>
            ) : (
              <TextArea
                value={form.returnAddress ?? ""}
                onChange={(v) => set({ returnAddress: v })}
                onBlur={flush}
                disabled={!canEdit}
                rows={4}
                maxLength={1000}
                placeholder={t("Company name\nWarehouse or office\nStreet and number\nPostcode City, Country")}
                data-testid="return-address"
              />
            )}
          </Field>
        </Grid>
        {/* the company's return terms for buyers: a link to its own page, its own text, or Aldoo's standard policy */}
        <ReturnPolicyField form={form} set={set} flush={flush} errors={errors} canEdit={canEdit} />
      </Section>

      <Section title={<span className="inline-flex items-center gap-1.5">{t`Shipping area`}<HelpHint helpKey="vendor.delivery#shipping-area" /></span>} collapsible>
        <Grid cols={2}>
          <Field label={t`Ships to`} hint={t`Where your physical products can be delivered. Every product uses this unless its own Shipping tab says otherwise.`}>
            <Select value={shipsTo.mode} options={SHIPS_TO_MODES.map((mode) => ({ ...mode, label: t(mode.label) }))}onChange={(mode) => setShipsTo({ mode }, { now: true })} disabled={!canEdit} className="w-full" data-testid="ships-to-mode" />
          </Field>
          {shipsTo.mode === "countries" && (
            <Field label={t`Countries`} hint={t`Until one is picked, products ship worldwide. The picker searches by name.`}>
              <CountriesSelect value={shipsTo.countries} placeholder={t`Pick countries…`}onChange={(countries) => setShipsTo({ countries }, { now: true })} disabled={!canEdit} data-testid="ships-to-countries" />
            </Field>
          )}
        </Grid>
      </Section>

      <Section title={<span className="inline-flex items-center gap-1.5">{t`Minimum order`}<HelpHint helpKey="vendor.delivery#minimum-order" /></span>} collapsible>
        <Grid cols={2}>
          <Field label={t`Minimum order value`} hint={t`The least an order from you may come to, before delivery. Below it buyers cannot check out. 0 means no minimum.`}>
            <div className="flex items-center gap-2" data-testid="min-order-amount">
              <MoneyInput value={minOrder.amount} onChange={(amount) => setMinOrder({ amount })} onBlur={flush} disabled={!canEdit} />
              <Select value={minOrderCurrency} options={currencyOptions({ currencies })} onChange={(v) => setMinOrder({ currency: v }, { now: true })} disabled={!canEdit} className="w-[110px]" />
            </div>
          </Field>
        </Grid>
      </Section>

      <Section title={<span className="inline-flex items-center gap-1.5">{t`Free shipping`}<HelpHint helpKey="vendor.delivery#free-shipping" /></span>} collapsible>
        <Grid cols={2}>
          <Field label={t`Free shipping threshold`} hint={t`What a buyer has to spend with you before you pay the delivery. 0 means you never do.`}>
            <div className="flex items-center gap-2">
              <MoneyInput value={threshold.amount} onChange={(amount) => setThreshold({ amount })} onBlur={flush} disabled={!canEdit} />
              <Select value={thresholdCurrency} options={currencyOptions({ currencies })} onChange={(v) => setThreshold({ currency: v }, { now: true })} disabled={!canEdit} className="w-[110px]" />
            </div>
          </Field>
        </Grid>
      </Section>

      {/* an order on its way completes by itself (aldoo-na/store/deliveries): when the courier says
          it was delivered, else after these days — never while the parcel is coming back, or while
          the buyer asked to return or cancel and that is not settled */}
      <Section title={<span className="inline-flex items-center gap-1.5">{t`Completing delivered orders`}<HelpHint helpKey="vendor.delivery#auto-complete" /></span>} collapsible>
        <Grid cols={2}>
          <Field
            label={t`Complete orders automatically`}
            hint={t`When the courier confirms delivery from the tracking number, the order completes on that day. Without tracking, it completes after the days set here.`}
          >
            <div className="flex items-center gap-3" data-testid="auto-complete-toggle">
              <ToggleSwitch variant="admin" isOn={form.autoCompleteDelivered !== false} onChange={(value) => canEdit && set({ autoCompleteDelivered: !!value }, { now: true })} />
              <span className="text-sm">{form.autoCompleteDelivered !== false ? t`On` : t`Off`}</span>
            </div>
          </Field>
          {form.autoCompleteDelivered !== false && (
            <Field
              label={t`Days after shipping`}
              hint={t`From 3 to 60. Not when the parcel is coming back, or while a return or cancellation request is open.`}
            >
              <NumberInput value={form.autoCompleteDays || 14} onChange={(v) => set({ autoCompleteDays: v })} onBlur={flush} disabled={!canEdit} min={3} max={60} data-testid="auto-complete-days" />
            </Field>
          )}
        </Grid>
      </Section>
      </>
      )}
    </>
  )
}

/**
 * A member row's action, as an outlined chip. They used to appear on hover as
 * plain words, which is a thing to find rather than a thing to see.
 */
export const RowAction = ({ onClick, danger, children }) => (
  <button
    onClick={onClick}
    className={`px-2.5 py-1 rounded-full border text-xs transition-colors ${
      danger
        ? "border-admin_error/40 text-admin_error hover:bg-admin_error hover:text-admin_text_inv hover:border-admin_error"
        : "border-admin_bordered text-admin_text_secondary hover:bg-admin_primary hover:text-admin_text_inv hover:border-admin_primary"
    }`}
  >
    {children}
  </button>
)

/**
 * The team: who is in the company, what each may do, and the roles the company
 * gives (MemberAccess.jsx). `canManage`: may invite and remove members and set
 * their roles (the company's `vendor-members:write`); `isOrgAdmin`: an admin
 * of the company (or a platform admin) — the only one who makes admins,
 * removes them, and composes the company's own roles.
 */
export function MembersPanel({ profile, canManage, isOrgAdmin = canManage, onChanged }) {
  const t = usePortalTexts(COMPANY)
  const { org, members, invites } = profile
  const [invite, setInvite] = useState(false)
  const [access, setAccess] = useState(null) // the member whose own permissions are open
  const { roles, reload: reloadRoles } = useOrgRoles(org._id)
  // the page's sub-tabs (team=members|roles), as Company's (company=…)
  const [tab, setTab] = useUrlParam("team", { default: "members" })
  const tabs = [
    { value: "members", label: t`Users` },
    { value: "roles", label: t`Roles` },
  ]
  const current = tabs.some((x) => x.value === tab) ? tab : "members"
  const roleEditing = useState(null) // the Roles tab's editor: the New role button is the section's
  // the roles the invite dialog offers, and the pending invitations name
  const roleOptions = [...(roles?.builtin || []), ...(roles?.custom || [])].map((r) => ({ key: r.key, name: roleName(t, r), description: r.builtin ? t(r.description) : r.description }))
  // an administrator of the company (the membership itself, as RoleSelect's): an admin invites one, by email or by link
  const adminOption = { key: "admin", name: t`Administrator`, description: t`Everything in the company, the team and its roles included.` }
  const inviteRoles = isOrgAdmin ? [adminOption, ...roleOptions] : roleOptions
  const roleNameOf = (key) => [adminOption, ...roleOptions].find((r) => r.key === key)?.name || key
  const act = async (fn, ok) => {
    const res = await fn()
    showNotification({ message: errorMessage(res, t`Request failed`) || ok, className: errorMessage(res) ? "bg-admin_error text-white" : "bg-admin_success text-white" })
    onChanged()
  }
  // the dialog follows the member as the list reloads (their roles change under it)
  const accessMember = access ? members.find((m) => m.userId === access) : null
  return (
    <>
      <Section title={<span className="inline-flex items-center gap-1.5">{t("Team (%s)", members.length)}<HelpHint helpKey="vendor.members#roles" /></span>} actions={current === "members" ? canManage && <Button variant="admin_secondary" onClick={() => setInvite(true)}>{t`Invite users`}</Button> : isOrgAdmin && <Button variant="admin_secondary" onClick={() => roleEditing[1](false)} data-testid="role-new">{t`New role`}</Button>}>
        <SectionPicker tabs={tabs} value={current} onChange={setTab} title={t`Team`} testId="team-tabs-select" />
        <Tabs variant="admin" value={current} onChange={setTab} tabs={tabs} className="hidden lg:block" data-testid="team-tabs" />
        {current === "roles" && <RolesSection orgId={org._id} roles={roles} canEdit={isOrgAdmin} onChanged={() => { reloadRoles(); onChanged() }} embedded editingState={roleEditing} />}
        {current === "members" && !members.length && <Empty>{t`No members`}</Empty>}
        {current === "members" && <div className="divide-y divide-admin_bordered/60 text-sm">
          {members.map((m) => (
            <div key={m.userId} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2 lg:grid lg:grid-cols-[minmax(0,1fr)_110px_260px_110px_90px]">
              {/* fixed columns from lg up: name | joined | role (Administrator among them) | permissions | remove; wrapped below */}
              <div className="min-w-0 flex-1 basis-[200px] lg:flex-none"><div className="font-medium truncate">{m.name}</div><div className="text-xs text-admin_text_secondary truncate">{m.email}</div></div>
              <span className="text-xs text-admin_text_secondary whitespace-nowrap">{dateOnly(m.joinedAt)}</span>
              <div className="w-[260px] shrink-0 lg:w-auto">
                <RoleSelect orgId={org._id} member={m} roles={roles} canManage={canManage && !m.isSelf} isOrgAdmin={isOrgAdmin} onChanged={onChanged} />
              </div>
              <div className="text-xs">
                {m.role !== "admin" && (
                  <RowAction onClick={() => setAccess(m.userId)}>
                    {m.permissions?.length ? t("permissions (%s)", m.permissions.length) : t`permissions`}
                  </RowAction>
                )}
              </div>
              {/* Removing yourself is how an organization ends up with nobody
                  running it, so it stays off your own row. Removing an admin
                  is the admins' own. */}
              <div className="flex justify-end text-xs">
                {canManage && !m.isSelf && (m.role !== "admin" || isOrgAdmin) && (
                  <RowAction danger onClick={() => showPromptDialog({ title: t`Remove member`, message: t("Remove %s from %s?", m.email, org.name), confirmText: t`Remove`, cancelText: t`Cancel`, onConfirm: () => act(() => RemoveMember(org._id, m.userId), t`Member removed`) })}>{t`remove`}</RowAction>
                )}
              </div>
            </div>
          ))}
        </div>}
        {roles?.error && <div className="text-sm text-admin_error">{roles.error}</div>}
      </Section>
      {accessMember && <MemberAccessDialog orgId={org._id} member={accessMember} roles={roles} canManage={canManage && !accessMember.isSelf} onClose={() => setAccess(null)} onChanged={onChanged} />}
      {current === "members" && invites.length > 0 && (
        <Section title={<span className="inline-flex items-center gap-1.5">{t("Pending invitations (%s)", invites.length)}<HelpHint helpKey="vendor.members#invitations" /></span>}>
          <div className="divide-y divide-admin_bordered/60 text-sm">
            {invites.map((inv) => (
              <div key={inv.inviteId} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-1.5">
                <span className="flex-1 basis-[200px] truncate">{inv.email}</span>
                {inv.roles?.length > 0 && (
                  <span className="flex flex-wrap gap-1">
                    {inv.roles.map((key) => (
                      <Chip key={key} className="bg-admin_neutral_1 text-admin_text">{roleNameOf(key)}</Chip>
                    ))}
                  </span>
                )}
                <span className="text-xs text-admin_text_secondary">{t("expires %s", dateOnly(inv.expiresAt))}</span>
                {canManage && <RowAction onClick={() => act(() => ResendInvite(org._id, inv.inviteId), t`Invitation resent`)}>{t`resend`}</RowAction>}
                {canManage && <RowAction danger onClick={() => act(() => RemoveInvite(org._id, inv.inviteId), t`Invitation removed`)}>{t`remove`}</RowAction>}
              </div>
            ))}
          </div>
        </Section>
      )}
      {/* an aldoo.com organization is a vendor, not a workspace: it has no
          teams, so the dialog asks for addresses and nothing else */}
      {invite && <InviteUsersDialog orgId={org._id} variant="admin" showTeams={false} roles={inviteRoles} onClose={() => { setInvite(false); onChanged() }} />}
    </>
  )
}

/**
 * Creating an organization asks one thing — its name — so it asks in a dialog
 * rather than growing a form inside the list. The company id is the API's to
 * give (the name slugged, with a short hash when that slug is taken), and a
 * name another organization already holds is confirmed first.
 */
